import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Filter,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  BarChart2,
  X,
  Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

interface FAQCategory {
  id: string;
  name: string;
  slug: string;
}

interface FAQSubcategory {
  id: string;
  category_id: string;
  name: string;
  slug: string;
}

interface FAQItem {
  id: string;
  category_id: string;
  subcategory_id: string | null;
  question: string;
  answer: string;
  keywords: string[] | null;
  sort_order: number;
  is_active: boolean;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  created_at: string;
  updated_at: string;
}

export function FAQItemsPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingItem, setEditingItem] = useState<FAQItem | null>(null);

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['admin-faq-categories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_categories')
        .select('id, name, slug')
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as FAQCategory[];
    },
  });

  // Fetch subcategories
  const { data: subcategories } = useQuery({
    queryKey: ['admin-faq-subcategories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_subcategories')
        .select('id, category_id, name, slug')
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as FAQSubcategory[];
    },
  });

  // Fetch FAQ items
  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-faq-items', selectedCategory, contentLocale],
    queryFn: async () => {
      let query = supabase
        .from('faq_items')
        .select('*')
        .eq('locale', contentLocale)
        .order('sort_order');

      if (selectedCategory) {
        query = query.eq('category_id', selectedCategory);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FAQItem[];
    },
  });

  // Create item mutation
  const createItem = useMutation({
    mutationFn: async (data: Partial<FAQItem>) => {
      const existingItems = items?.filter(i => i.category_id === data.category_id) || [];
      const { error } = await (supabase.from('faq_items') as any).insert({
        category_id: data.category_id,
        subcategory_id: data.subcategory_id || null,
        question: data.question,
        answer: data.answer,
        keywords: data.keywords || [],
        sort_order: existingItems.length + 1,
        is_active: true,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
      setIsCreating(false);
      toast.success('FAQ erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Update item mutation
  const updateItem = useMutation({
    mutationFn: async (data: FAQItem) => {
      const { error } = await (supabase.from('faq_items') as any)
        .update({
          category_id: data.category_id,
          subcategory_id: data.subcategory_id || null,
          question: data.question,
          answer: data.answer,
          keywords: data.keywords || [],
          is_active: data.is_active,
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
      setEditingItem(null);
      toast.success('FAQ aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete item mutation
  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('faq_items').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
      toast.success('FAQ gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Toggle active
  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase.from('faq_items') as any)
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-items'] });
    },
  });

  // Filter items
  const filteredItems = items?.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.question.toLowerCase().includes(query) ||
      item.answer.toLowerCase().includes(query) ||
      item.keywords?.some(k => k.toLowerCase().includes(query))
    );
  });

  const getCategoryName = (categoryId: string) => {
    return categories?.find(c => c.id === categoryId)?.name || '-';
  };

  const getSubcategoryName = (subcategoryId: string | null) => {
    if (!subcategoryId) return null;
    return subcategories?.find(s => s.id === subcategoryId)?.name;
  };

  // Stats
  const totalViews = items?.reduce((sum, i) => sum + i.view_count, 0) || 0;
  const totalHelpful = items?.reduce((sum, i) => sum + i.helpful_count, 0) || 0;
  const totalNotHelpful = items?.reduce((sum, i) => sum + i.not_helpful_count, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">FAQ-Einträge</h1>
            <p className="text-slate-500 mt-1">
              Verwalte Fragen und Antworten
            </p>
          </div>
          <LocaleSelector />
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                       hover:bg-teal-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Neue FAQ
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-900">{items?.length || 0}</div>
            <div className="text-sm text-slate-500">Gesamt FAQs</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold text-slate-900">{totalViews}</span>
            </div>
            <div className="text-sm text-slate-500">Aufrufe</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold text-slate-900">{totalHelpful}</span>
            </div>
            <div className="text-sm text-slate-500">Hilfreich</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ThumbsDown className="w-5 h-5 text-red-500" />
              <span className="text-2xl font-bold text-slate-900">{totalNotHelpful}</span>
            </div>
            <div className="text-sm text-slate-500">Nicht hilfreich</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suche nach Fragen, Antworten, Keywords..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-slate-200 rounded-lg appearance-none
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">Alle Kategorien</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Create Form */}
      {isCreating && (
        <FAQItemForm
          categories={categories || []}
          subcategories={subcategories || []}
          onSubmit={(data) => createItem.mutate(data)}
          onCancel={() => setIsCreating(false)}
          isLoading={createItem.isPending}
        />
      )}

      {/* Edit Form */}
      {editingItem && (
        <FAQItemForm
          item={editingItem}
          categories={categories || []}
          subcategories={subcategories || []}
          onSubmit={(data) => updateItem.mutate({ ...editingItem, ...data })}
          onCancel={() => setEditingItem(null)}
          isLoading={updateItem.isPending}
          isEditing
        />
      )}

      {/* Items List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : filteredItems?.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {searchQuery ? 'Keine Ergebnisse gefunden' : 'Noch keine FAQ-Einträge vorhanden'}
          </div>
        ) : (
          filteredItems?.map((item) => (
            <div key={item.id} className="p-4 hover:bg-slate-50">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-medium ${!item.is_active && 'text-slate-400'}`}>
                      {item.question}
                    </span>
                    {!item.is_active && (
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                        Inaktiv
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                    {item.answer}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="bg-slate-100 px-2 py-0.5 rounded">
                      {getCategoryName(item.category_id)}
                    </span>
                    {getSubcategoryName(item.subcategory_id) && (
                      <span className="bg-slate-100 px-2 py-0.5 rounded">
                        {getSubcategoryName(item.subcategory_id)}
                      </span>
                    )}
                    {item.keywords && item.keywords.length > 0 && (
                      <span className="text-slate-400">
                        Keywords: {item.keywords.slice(0, 3).join(', ')}
                        {item.keywords.length > 3 && ` +${item.keywords.length - 3}`}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {/* Stats */}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <BarChart2 className="w-3.5 h-3.5" />
                      {item.view_count}
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      <ThumbsUp className="w-3.5 h-3.5" />
                      {item.helpful_count}
                    </span>
                    <span className="flex items-center gap-1 text-red-500">
                      <ThumbsDown className="w-3.5 h-3.5" />
                      {item.not_helpful_count}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleActive.mutate({
                        id: item.id,
                        is_active: !item.is_active
                      })}
                      className={`p-1.5 rounded transition-colors ${
                        item.is_active
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-slate-400 hover:bg-slate-100'
                      }`}
                      title={item.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingItem(item)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Diese FAQ wirklich löschen?')) {
                          deleteItem.mutate(item.id);
                        }
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// FAQ Item Form Component
interface FAQItemFormProps {
  item?: FAQItem;
  categories: FAQCategory[];
  subcategories: FAQSubcategory[];
  onSubmit: (data: Partial<FAQItem>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

function FAQItemForm({
  item,
  categories,
  subcategories,
  onSubmit,
  onCancel,
  isLoading,
  isEditing
}: FAQItemFormProps) {
  const [categoryId, setCategoryId] = useState(item?.category_id || '');
  const [subcategoryId, setSubcategoryId] = useState(item?.subcategory_id || '');
  const [question, setQuestion] = useState(item?.question || '');
  const [answer, setAnswer] = useState(item?.answer || '');
  const [keywordsInput, setKeywordsInput] = useState(item?.keywords?.join(', ') || '');

  const availableSubcategories = subcategories.filter(s => s.category_id === categoryId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const keywords = keywordsInput
      .split(',')
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0);

    onSubmit({
      category_id: categoryId,
      subcategory_id: subcategoryId || null,
      question,
      answer,
      keywords,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">
          {isEditing ? 'FAQ bearbeiten' : 'Neue FAQ erstellen'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Kategorie *
            </label>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId('');
              }}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">Kategorie wählen...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Unterkategorie (optional)
            </label>
            <select
              value={subcategoryId}
              onChange={(e) => setSubcategoryId(e.target.value)}
              disabled={!categoryId || availableSubcategories.length === 0}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500
                         disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">Keine Unterkategorie</option>
              {availableSubcategories.map((sub) => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Frage *
          </label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            placeholder="Wie funktioniert...?"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Antwort *
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            required
            rows={6}
            placeholder="Die ausführliche Antwort auf die Frage..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Keywords (kommagetrennt)
          </label>
          <input
            type="text"
            value={keywordsInput}
            onChange={(e) => setKeywordsInput(e.target.value)}
            placeholder="login, passwort, anmelden"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          <p className="text-xs text-slate-400 mt-1">
            Verbessert die Suche. Wird automatisch kleingeschrieben.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading || !categoryId || !question || !answer}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                     hover:bg-teal-600 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          {isEditing ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}
